/**
 * Energy Role - Format Pillar
 *
 * Formata valores de energia para apresentação.
 *
 * @example
 * import { formatEnergy } from '@attrx/role-morphic/energy/format';
 *
 * formatEnergy('kilowatt_hour', 2.5);                      // "2.5 kWh"
 * formatEnergy('kilowatt_hour', 2.5, { verbose: true });   // "2.5 kilowatt-hours"
 * formatEnergy('joule', 1000, { locale: 'pt-BR' });        // "1.000 J"
 */

import { ENERGY_UNITS, type EnergyUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface EnergyFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de energia para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatEnergy('kilowatt_hour', 2.5);                        // "2.5 kWh"
 * formatEnergy('kilowatt_hour', 2.5, { verbose: true });     // "2.5 kilowatt-hours"
 * formatEnergy('kilowatt_hour', 1, { verbose: true });       // "1 kilowatt-hour"
 * formatEnergy('joule', 1000, { locale: 'pt-BR' });          // "1.000 J"
 * formatEnergy('kilocalorie', 1500, { decimals: 0 });        // "1500 kcal"
 */
export function formatEnergy(
  variant: EnergyUnit,
  value: number,
  options: EnergyFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = ENERGY_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown energy variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getEnergySymbol('kilowatt_hour');  // "kWh"
 * getEnergySymbol('kilocalorie');    // "kcal"
 */
export function getEnergySymbol(variant: EnergyUnit): string {
  const unit = ENERGY_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown energy variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getEnergyName('kilowatt_hour', 1);   // "kilowatt-hour"
 * getEnergyName('kilowatt_hour', 2);   // "kilowatt-hours"
 */
export function getEnergyName(variant: EnergyUnit, count: number = 1): string {
  const unit = ENERGY_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown energy variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
